// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import updateShipTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateShipTo";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import PlaceOrderButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import SubmitForApprovalButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitSubmitForApprovalButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xCommonLoadingOverlay } from "@plumberssupply/App/Handlers/XNG/Common";
import { getIsExpressCheckoutPage } from "@plumberssupply/App/Store/Selectors/GeneralSelectors";
import {
    CheckoutStep,
    CHECKOUT_STEPS,
    nextStepHandler,
    previousStepHandler,
} from "@plumberssupply/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@plumberssupply/App/Widgets/CheckoutStepper/StepperContext";
import { CheckoutShippingFormContext } from "@plumberssupply/Overrides/Pages/CheckoutShippingPage";
import { color, doScroll, xScreen } from "@plumberssupply/XDL/Utilities";
import cloneDeep from "lodash/cloneDeep";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

interface OwnProps {}

const mapDispatchToProps = {
    xCommonLoadingOverlay,
    updateShipTo,
    setAddress: (address: ShipToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/CheckoutShipping/SetShippingAddress",
            address,
        });
    },
    updateCart: makeHandlerChainAwaitable(updateCart),
    setInitialValues,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutStepperStyles {
    container?: InjectableCss;
    prev?: ButtonPresentationProps;
    next?: ButtonPresentationProps;
}

export const styles: CheckoutStepperStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            margin-top: 40px;
            padding-top: 24px;
            border-top: 1px solid ${color("common.ui7")};

            ${xScreen("lte", "xs")} {
                flex-direction: column-reverse;
                justify-content: center;
            }

            ${({ isExpressCheckoutPage }: any) => {
                if (isExpressCheckoutPage) {
                    return css`
                        justify-content: flex-end;
                        margin-top: 28px;
                    `;
                }
            }}

            ${({ checkoutReviewPage }: any) => {
                if (checkoutReviewPage) {
                    return css`
                        margin-top: 28px;
                    `;
                }
            }}
        `,
    },
    prev: {
        variant: "secondary",
        sizeVariant: "small",
        mergeCss: true,
        css: css`
            ${xScreen("lte", "xs")} {
                margin-top: 20px;
            }
        `,
    },
    next: {
        variant: "primary",
    },
};

const CheckoutStepper: React.FC<Props> = props => {
    const { currentStep, steps, setCurrentStep, checkoutInfoState } = useContext(StepperContext);
    const { validators } = useContext(CheckoutShippingFormContext);

    const history = useHistory();

    const {
        cartPage,
        checkoutShippingPage,
        isLoading,
        cart,
        showPlaceOrderButton,
        showSubmitForApprovalOrder,
        shipToState,
        requestedDeliveryDate,
        useOneTimeAddress,
        reviewAndSubmitPageLink,
        isShippingAddressUpdateRequired,
        isBillingAddressUpdateRequired,
        isExpressCheckoutPage,
        checkoutReviewPage,
        requiresApproval,
    } = useAppSelector(state => {
        const { cartId } = getQueryStrings(state);
        const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
        return {
            checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
            cartPage: getPageLinkByPageType(state, "CartPage"),
            isLoading: cartState.isLoading,
            cart: cartState.value,
            showPlaceOrderButton: canPlaceOrder(cartState.value),
            showSubmitForApprovalOrder: canSubmitForApprovalOrder(getCurrentCartState(state).value),
            shipToState: getShipToState(state, cartState?.value ? cartState.value.shipToId : undefined),
            requestedDeliveryDate: state.pages.checkoutReviewAndSubmit.requestedDeliveryDate,
            useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
            shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
            reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
            isShippingAddressUpdateRequired: state.pages.checkoutShipping.isShippingAddressUpdateRequired,
            isBillingAddressUpdateRequired: state.pages.checkoutShipping.isBillingAddressUpdateRequired,
            isExpressCheckoutPage: getIsExpressCheckoutPage(state),
            checkoutReviewPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
            requiresApproval: cartState.value?.requiresApproval,
        };
    });

    const goToNextStep = async (nextStep: CheckoutStep) => {
        if (currentStep === CHECKOUT_STEPS.ADDRESS) {
            if (isBillingAddressUpdateRequired && validators.billingAddress) {
                const isValid = await validators.billingAddress();
                if (!isValid) {
                    return;
                }
            }

            if (cart?.fulfillmentMethod === FulfillmentMethod.Ship) {
                const shipTo = cloneDeep(shipToState.value);
                // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                shipTo!.properties!.plumbers_DeliveryDate = requestedDeliveryDate?.toISOString()!;

                await props.updateShipTo({
                    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                    billToId: cart?.billToId!,
                    shipTo: shipTo!,
                    onSuccess: hasCartlines => {},
                    isEdit: true,
                });
            }

            await props.updateCart({});
            await props.setInitialValues({});

            const continueUrl = cart?.id
                ? `${reviewAndSubmitPageLink!.url}?cartId=${cart.id}`
                : reviewAndSubmitPageLink!.url;
            history.push(continueUrl);
        }

        if (currentStep !== CHECKOUT_STEPS.ADDRESS) {
            setCurrentStep(nextStep.index);
        }

        doScroll(true);
    };

    const goToPrevStep = (prevStep: CheckoutStep) => {
        if (currentStep === CHECKOUT_STEPS.ADDRESS) {
            history.push(cartPage?.url as string);
        } else if (prevStep.index === CHECKOUT_STEPS.ADDRESS) {
            history.push(checkoutShippingPage?.url as string);
        } else {
            setCurrentStep(prevStep.index);
        }

        doScroll(true);
    };

    const prevStep = previousStepHandler(steps, currentStep);
    const nextStep = nextStepHandler(steps, currentStep);

    const isNextButtonDisabled = () => {
        if (
            currentStep < 0 ||
            isLoading ||
            (checkoutInfoState.invalidStep && !requiresApproval) ||
            isShippingAddressUpdateRequired
        ) {
            return true;
        }

        return false;
    };

    if (isExpressCheckoutPage) {
        return (
            <StyledWrapper {...styles.container} {...{ isExpressCheckoutPage }}>
                {cart && showPlaceOrderButton && <PlaceOrderButton styles={styles.next} />}
                {cart && showSubmitForApprovalOrder && <SubmitForApprovalButton extendedStyles={styles.next} />}
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.container} {...{ currentStep }} {...{ checkoutReviewPage }}>
            <Button {...styles.prev} onClick={() => goToPrevStep(prevStep)} disabled={isLoading} type="button">
                {`${currentStep === CHECKOUT_STEPS.ADDRESS ? translate("Back to Cart") : translate("Previous")}`}
            </Button>

            {currentStep !== CHECKOUT_STEPS.REVIEW && (
                <Button
                    {...styles.next}
                    onClick={() => goToNextStep(nextStep)}
                    disabled={isNextButtonDisabled()}
                    type="button"
                >
                    {`${translate("Next")} : ${nextStep.name}`}
                </Button>
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showPlaceOrderButton && (
                <PlaceOrderButton styles={styles.next} />
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showSubmitForApprovalOrder && (
                <SubmitForApprovalButton extendedStyles={styles.next} />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CheckoutStepper),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage", "ExpressCheckoutPage"] as any,
        displayName: "Checkout Stepper Actions",
    },
};

export default widgetModule;
